// Product gallery slider: one image per view. Prev/next chevrons (--prev / --next commands) and
// optional thumbnails (--go with data-index) scroll the track; a counter and button states follow
// the slide in view. Swipe and trackpad scrolling work natively via scroll-snap.
class ProductGallery extends HTMLElement {
  #controller = null;
  #observer = null;
  #active = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const track = this.querySelector('[data-gallery-track]');
    if (!track) return;
    const slides = Array.from(track.querySelectorAll('[data-gallery-slide]'));
    const thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    const current = this.querySelector('[data-gallery-current]');

    const goTo = (index) => {
      const slide = slides[Math.max(0, Math.min(index, slides.length - 1))];
      if (!slide) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    };

    const sync = (active) => {
      this.#active = active;
      if (current) current.textContent = String(active + 1);
      if (prev) prev.disabled = active <= 0;
      if (next) next.disabled = active >= slides.length - 1;
      thumbs.forEach((thumb) => {
        const index = Number(thumb.dataset.index);
        const isActive = index === active || (thumb.hasAttribute('data-gallery-more') && active >= index);
        thumb.toggleAttribute('data-active', isActive);
        if (isActive) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    };

    this.addEventListener(
      'command',
      (event) => {
        if (event.command === '--prev') goTo(this.#active - 1);
        if (event.command === '--next') goTo(this.#active + 1);
        if (event.command === '--go') goTo(Number(event.source?.dataset.index || 0));
      },
      { signal }
    );

    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) sync(slides.indexOf(entry.target));
        });
      },
      { root: track, threshold: 0.6 }
    );
    slides.forEach((slide) => this.#observer.observe(slide));
    sync(0);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', ProductGallery);
}
